import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import PrivateRoute from '@/components/common/PrivateRoute';
import PublicRoute from '@/components/common/PublicRoute';
import PageLoader from '@/components/common/PageLoader';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import Error404 from '@/components/common/Error404';
import { ROUTES } from '@/utils/constants';
import KipiverseInquirysPage from '@/features/kipiverseInquirys/KipiverseInquirysPage';
import EditKipiverseInquiryPage from '@/features/kipiverseInquirys/EditKipiverseInquiryPage';
import ModuleDocAiPage, { AddEditModuleDocAiPage } from '@/features/moduleDocAi';

// ──────────────────────────────────────────────
// Lazy imports — Auth
// ──────────────────────────────────────────────
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'));
const OtpPage = lazy(() => import('@/features/auth/OtpPage'));
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'));

// ──────────────────────────────────────────────
// Lazy imports — Protected pages
// ──────────────────────────────────────────────
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const SubscriptionDashboardPage = lazy(
  () => import('@/features/subscriptionDashboard/SubscriptionDashboardPage'),
);
const SubscriptionAnalysisPage = lazy(
  () => import('@/features/subscriptionAnalysis/SubscriptionAnalysisPage'),
);
const DashboardAnalyticsPage = lazy(() => import('@/features/appAnalytics/DashboardAnalyticsPage'));
const EngagementAnalyticsPage = lazy(
  () => import('@/features/appAnalytics/EngagementAnalyticsPage'),
);
const VersionAnalyticsPage = lazy(() => import('@/features/appAnalytics/VersionAnalyticsPage'));
const PatternAnalyticsPage = lazy(() => import('@/features/appAnalytics/PatternAnalyticsPage'));
const UserAnalyticsPage = lazy(() => import('@/features/appAnalytics/UserAnalyticsPage'));
const SchoolInstituteAnalyticsPage = lazy(
  () => import('@/features/appAnalytics/SchoolInstituteAnalyticsPage'),
);
const EntityPage = lazy(() => import('@/features/entity/EntityPage'));
const EntityTypePage = lazy(() => import('@/features/entity/EntityTypePage'));
const AddEditEntityTypePage = lazy(() => import('@/features/entity/AddEditEntityTypePage'));
const AddEditEntityPage = lazy(() => import('@/features/entity/AddEditEntityPage'));
const AcademicCalendarPage = lazy(() => import('@/features/entity/AcademicCalendarPage'));
const AddEditAcademicCalendarPage = lazy(
  () => import('@/features/entity/AddEditAcademicCalendarPage'),
);
const IndexManagementPage = lazy(() => import('@/features/indexManagement/IndexManagementPage'));
const InstituteListPage = lazy(() => import('@/features/institute/InstituteListPage'));
const SchoolListPage = lazy(() => import('@/features/institute/SchoolListPage'));
const DeletedSchoolListPage = lazy(() => import('@/features/institute/DeletedSchoolListPage'));
const DeletedInstituteListPage = lazy(
  () => import('@/features/institute/DeletedInstituteListPage'),
);
const CollegeListPage = lazy(() => import('@/features/institute/CollegeListPage'));
const DeletedCollegeListPage = lazy(
  () => import('@/features/institute/DeletedCollegeListPage'),
);
const HospitalListPage = lazy(() => import('@/features/hospital/HospitalListPage'));
const InstituteDetailPage = lazy(() => import('@/features/institute/InstituteDetailPage'));
const AssignPlanPage = lazy(() => import('@/features/institute/AssignPlanPage'));
const TeacherListPage = lazy(() => import('@/features/institute/TeacherListPage'));
const StudentListPage = lazy(() => import('@/features/institute/StudentListPage'));
// Approvals
const SchoolApprovalsPage = lazy(() => import('@/features/approvals/SchoolApprovalsPage'));
const InstituteApprovalsPage = lazy(() => import('@/features/approvals/InstituteApprovalsPage'));
const CollegeApprovalsPage = lazy(() => import('@/features/approvals/CollegeApprovalsPage'));
const TeacherApprovalsPage = lazy(() => import('@/features/approvals/TeacherApprovalsPage'));
const StudentApprovalsPage = lazy(() => import('@/features/approvals/StudentApprovalsPage'));
const SportsAcademyApprovalsPage = lazy(
  () => import('@/features/approvals/SportsAcademyApprovalsPage'),
);
const TrainerApprovalsPage = lazy(() => import('@/features/approvals/TrainerApprovalsPage'));
const AthleteApprovalsPage = lazy(() => import('@/features/approvals/AthleteApprovalsPage'));
const AgencyApprovalsPage = lazy(() => import('@/features/approvals/AgencyApprovalsPage'));
const HospitalApprovalsPage = lazy(() => import('@/features/approvals/HospitalApprovalsPage'));
const SportsAmenitiesApprovalsPage = lazy(
  () => import('@/features/approvals/SportsAmenitiesApprovalsPage'),
);
const ExpoListPage = lazy(() => import('@/features/expo/ExpoListPage'));
const AddEditExpoPage = lazy(() => import('@/features/expo/AddEditExpoPage'));
const ExpoDetailPage = lazy(() => import('@/features/expo/ExpoDetailPage'));
const ExpoStaffPage = lazy(() => import('@/features/expo/ExpoStaffPage'));
const ExpoGridPage = lazy(() => import('@/features/expoGrid/ExpoGridPage'));
const ExpoStallPage = lazy(() => import('@/features/expoStall/ExpoStallPage'));
const ExpoStageSlotPage = lazy(() => import('@/features/expoStageSlot/ExpoStageSlotPage'));
const AddEditExpoStageSlotPage = lazy(() => import('@/features/expoStageSlot/AddEditExpoStageSlotPage'));
const ApprovalDetailPage = lazy(() => import('@/features/approvals/ApprovalDetailPage'));
const SubAdminPage = lazy(() => import('@/features/users/SubAdminPage'));
const SubAdminAddEditPage = lazy(() => import('@/features/users/SubAdminAddEditPage.tsx'));
const TeachersPage = lazy(() => import('@/features/users/TeachersPage'));
const StudentsPage = lazy(() => import('@/features/users/StudentsPage'));
const ParentsPage = lazy(() => import('@/features/users/ParentsPage'));
const InstituteAdminsPage = lazy(() => import('@/features/users/InstituteAdminsPage'));
const InstituteMasterAdminsPage = lazy(() => import('@/features/users/InstituteMasterAdminsPage'));
const SchoolAdminsPage = lazy(() => import('@/features/users/SchoolAdminsPage'));
const AdminsPage = lazy(() => import('@/features/users/AdminsPage'));
const MasterAdminsPage = lazy(() => import('@/features/users/MasterAdminsPage'));
const GenericUserListPage = lazy(() => import('@/features/users/GenericUserListPage'));
const DriversPage = () => (
  <GenericUserListPage
    userType="DRIVER"
    title="Drivers"
    subtitle="All driver accounts"
    searchPlaceholder="Search drivers…"
    emptyMessage="No drivers found."
  />
);
const VendorsPage = () => (
  <GenericUserListPage
    userType="VENDOR"
    title="Vendors"
    subtitle="All vendor accounts"
    searchPlaceholder="Search vendors…"
    emptyMessage="No vendors found."
  />
);
const SportInstituteOwnersPage = () => (
  <GenericUserListPage
    userType="SPORT_INSTITUTE_OWNER"
    title="Sport Institute Owners"
    subtitle="All sport institute owner accounts"
    searchPlaceholder="Search sport institute owners…"
    emptyMessage="No sport institute owners found."
  />
);
const SportTrainersPage = () => (
  <GenericUserListPage
    userType="SPORT_TRAINER"
    title="Sport Trainers"
    subtitle="All sport trainer accounts"
    searchPlaceholder="Search sport trainers…"
    emptyMessage="No sport trainers found."
  />
);
const SportAthletesPage = () => (
  <GenericUserListPage
    userType="SPORT_ATHLETE"
    title="Sport Athletes"
    subtitle="All sport athlete accounts"
    searchPlaceholder="Search sport athletes…"
    emptyMessage="No sport athletes found."
  />
);
const DeoDpeoPage = () => (
  <GenericUserListPage
    userType="DEO_DPEO"
    title="DEO / DPEO"
    subtitle="All DEO / DPEO accounts"
    searchPlaceholder="Search DEO / DPEO accounts…"
    emptyMessage="No DEO / DPEO accounts found."
  />
);
const DeoDpeoStaffPage = () => (
  <GenericUserListPage
    userType="DEO_DPEO_STAFF"
    title="DEO / DPEO Staff"
    subtitle="All DEO / DPEO staff accounts"
    searchPlaceholder="Search DEO / DPEO staff…"
    emptyMessage="No DEO / DPEO staff found."
  />
);
const AgencyMasterAdminsPage = () => (
  <GenericUserListPage
    userType="AGENCY_MASTER_ADMIN"
    title="Agency Master Admins"
    subtitle="All agency master admin accounts"
    searchPlaceholder="Search agency master admins…"
    emptyMessage="No agency master admins found."
  />
);
const JobSeekersPage = () => (
  <GenericUserListPage
    userType="JOB_SEEKER"
    title="Job Seekers"
    subtitle="All job seeker accounts"
    searchPlaceholder="Search job seekers…"
    emptyMessage="No job seekers found."
  />
);
const RolesAndPermissionPage = lazy(() => import('@/features/roles/RolesAndPermissionPage'));
const MakeRolesAndPermissionPage = lazy(
  () => import('@/features/roles/MakeRolesAndPermissionPage'),
);
const PermissionsPage = lazy(() =>
  import('@/features/permissions').then((m) => ({ default: m.PermissionsPage })),
);
const CreatePermissionPage = lazy(() =>
  import('@/features/permissions').then((m) => ({ default: m.CreatePermissionPage })),
);
const RoleManagementPage = lazy(() =>
  import('@/features/role-management').then((m) => ({ default: m.RoleManagementPage })),
);
const CreateRoleManagementPage = lazy(() =>
  import('@/features/role-management').then((m) => ({ default: m.CreateRoleManagementPage })),
);
const AppPermissionsPage = lazy(() =>
  import('@/features/appRolesPermissions').then((m) => ({ default: m.AppPermissionsPage })),
);
const AppCreatePermissionPage = lazy(() =>
  import('@/features/appRolesPermissions').then((m) => ({ default: m.AppCreatePermissionPage })),
);
const AppRoleManagementPage = lazy(() =>
  import('@/features/appRolesPermissions').then((m) => ({ default: m.AppRoleManagementPage })),
);
const AppCreateRoleManagementPage = lazy(() =>
  import('@/features/appRolesPermissions').then((m) => ({ default: m.AppCreateRoleManagementPage })),
);
const AnnouncementPage = lazy(() => import('@/features/announcement/AnnouncementPage'));
const AddEditAnnouncementPage = lazy(
  () => import('@/features/announcement/AddEditAnnouncementPage'),
);
const ViewAnnouncementPage = lazy(() => import('@/features/announcement/ViewAnnouncementPage'));
const BannerPage = lazy(() => import('@/features/banner/BannerPage'));
const AddEditBannerPage = lazy(() => import('@/features/banner/AddEditBannerPage'));
const AdvertisementPage = lazy(() => import('@/features/advertisement/AdvertisementPage'));
const AddEditAdvertisementPage = lazy(
  () => import('@/features/advertisement/AddEditAdvertisementPage'),
);
const ViewAdvertisementPage = lazy(() => import('@/features/advertisement/ViewAdvertisementPage'));

const InformationPage = lazy(() =>
  import('@/features/information').then((m) => ({ default: m.InformationPage })),
);
const AddEditInformationPage = lazy(() =>
  import('@/features/information').then((m) => ({ default: m.AddEditInformationPage })),
);
const InformationDetailsPage = lazy(() =>
  import('@/features/information').then((m) => ({ default: m.InformationDetailsPage })),
);
const TypeManagementPage = lazy(() =>
  import('@/features/typeManagement').then((m) => ({ default: m.TypeManagementPage })),
);
const AddEditTypePage = lazy(() =>
  import('@/features/typeManagement').then((m) => ({ default: m.AddEditTypePage })),
);
const TermsAndConditionPage = lazy(() =>
  import('@/features/termsAndCondition').then((m) => ({ default: m.TermsAndConditionPage })),
);
const AddEditTermsAndConditionPage = lazy(() =>
  import('@/features/termsAndCondition').then((m) => ({ default: m.AddEditTermsAndConditionPage })),
);
const ViewTermsAndConditionPage = lazy(() =>
  import('@/features/termsAndCondition').then((m) => ({ default: m.ViewTermsAndConditionPage })),
);

const CertificatesManagementPage = lazy(
  () => import('@/features/certificates/CertificatesManagementPage'),
);
const AddEditCertificatePage = lazy(() => import('@/features/certificates/AddEditCertificatePage'));
const CustomCertificatesPage = lazy(
  () => import('@/features/customCertificates/CustomCertificatesPage'),
);
const AddEditCustomCertificatePage = lazy(
  () => import('@/features/customCertificates/AddEditCustomCertificatePage'),
);
const ElearningPage = lazy(() => import('@/features/elearning/ElearningPage'));
const CloneCoursePage = lazy(() => import('@/features/elearning/CloneCoursePage'));
const CourseIndexPage = lazy(() => import('@/features/elearning/CourseIndexPage'));
const CourseSetPlanPage = lazy(() => import('@/features/elearning/CourseSetPlanPage'));
const CreateCoursePage = lazy(() => import('@/features/elearning/CreateCoursePage'));
const CourseUploadMaterialPage = lazy(
  () => import('@/features/elearning/CourseUploadMaterialPage'),
);
const MergeCourseCreatePage = lazy(() => import('@/features/elearning/MergeCourseCreatePage'));
const MergeCourseIndexPage = lazy(() => import('@/features/elearning/MergeCourseIndexPage'));
const MergeCoursePlanPage = lazy(() => import('@/features/elearning/MergeCoursePlanPage'));
const AddEditSubscriptionPlanPage = lazy(
  () => import('@/features/subscriptionPlan/AddEditSubscriptionPlanPage'),
);
const SubscriptionPlanPage = lazy(() => import('@/features/subscriptionPlan/SubscriptionPlanPage'));
const SportsPage = lazy(() => import('@/features/arena/SportsPage'));
const AddEditSportPage = lazy(() => import('@/features/arena/AddEditSportPage'));
const SportCategoriesPage = lazy(() => import('@/features/arena/SportCategoriesPage'));
const AddEditSportCategoryPage = lazy(() => import('@/features/arena/AddEditSportCategoryPage'));
const SportSubCategoriesPage = lazy(() => import('@/features/arena/SportSubCategoriesPage'));
const AddEditSportSubCategoryPage = lazy(
  () => import('@/features/arena/AddEditSportSubCategoryPage'),
);
const SportRolesPage = lazy(() => import('@/features/arena/SportRolesPage'));
const AddEditSportRolePage = lazy(() => import('@/features/arena/AddEditSportRolePage'));
const EquipmentsPage = lazy(() => import('@/features/arena/EquipmentsPage'));
const AddEditEquipmentPage = lazy(() => import('@/features/arena/AddEditEquipmentPage'));
const FacilityMastersPage = lazy(() => import('@/features/arena/FacilityMastersPage'));
const AddEditFacilityMasterPage = lazy(() => import('@/features/arena/AddEditFacilityMasterPage'));
const FacilityAmenitiesPage = lazy(() => import('@/features/arena/FacilityAmenitiesPage'));
const AddEditFacilityAmenityPage = lazy(
  () => import('@/features/arena/AddEditFacilityAmenityPage'),
);
const LinkFacilitySportsPage = lazy(() =>
  import('@/features/arena/LinkEntitySportsPage').then((m) => ({
    default: m.LinkFacilitySportsPage,
  })),
);
const LinkAmenitySportsPage = lazy(() =>
  import('@/features/arena/LinkEntitySportsPage').then((m) => ({
    default: m.LinkAmenitySportsPage,
  })),
);
const LinkEquipmentSportsPage = lazy(() =>
  import('@/features/arena/LinkEntitySportsPage').then((m) => ({
    default: m.LinkEquipmentSportsPage,
  })),
);
const UnitsPage = lazy(() => import('@/features/arena/UnitsPage'));
const AddEditUnitPage = lazy(() => import('@/features/arena/AddEditUnitPage'));
const SkillsPage = lazy(() => import('@/features/arena/SkillsPage'));
const AddEditSkillPage = lazy(() => import('@/features/arena/AddEditSkillPage'));
const AgeGroupsPage = lazy(() => import('@/features/arena/AgeGroupsPage'));
const AddEditAgeGroupPage = lazy(() => import('@/features/arena/AddEditAgeGroupPage'));
const SportGroupDataPage = lazy(() => import('@/features/arena/SportGroupDataPage'));
const AddEditSportGroupDataPage = lazy(() => import('@/features/arena/AddEditSportGroupDataPage'));
const AssessmentTestsPage = lazy(() => import('@/features/arena/AssessmentTestsPage'));
const AddEditAssessmentTestPage = lazy(() => import('@/features/arena/AddEditAssessmentTestPage'));
const SportInfosPage = lazy(() => import('@/features/arena/SportInfosPage'));
const AddEditSportInfoPage = lazy(() => import('@/features/arena/AddEditSportInfoPage'));
const ArenaAnnouncementsPage = lazy(
  () => import('@/features/arena/announcements/ArenaAnnouncementsPage'),
);
const AddEditArenaAnnouncementPage = lazy(
  () => import('@/features/arena/announcements/AddEditArenaAnnouncementPage'),
);
const ViewArenaAnnouncementPage = lazy(
  () => import('@/features/arena/announcements/ViewArenaAnnouncementPage'),
);
const KipigovtAnnouncementsPage = lazy(
  () => import('@/features/kipigovt/announcements/KipigovtAnnouncementsPage'),
);
const AddEditKipigovtAnnouncementPage = lazy(
  () => import('@/features/kipigovt/announcements/AddEditKipigovtAnnouncementPage'),
);
const ViewKipigovtAnnouncementPage = lazy(
  () => import('@/features/kipigovt/announcements/ViewKipigovtAnnouncementPage'),
);
const VendorAnnouncementsPage = lazy(
  () => import('@/features/vendor/announcements/VendorAnnouncementsPage'),
);
const AddEditVendorAnnouncementPage = lazy(
  () => import('@/features/vendor/announcements/AddEditVendorAnnouncementPage'),
);
const ViewVendorAnnouncementPage = lazy(
  () => import('@/features/vendor/announcements/ViewVendorAnnouncementPage'),
);
const CoinOfferPage = lazy(() =>
  import('@/features/coinOffer').then((m) => ({ default: m.CoinOfferPage })),
);
const AddEditCoinOfferPage = lazy(() =>
  import('@/features/coinOffer').then((m) => ({ default: m.AddEditCoinOfferPage })),
);
const CloudStoragePage = lazy(() => import('@/features/cloudStorage/CloudStoragePage'));
const AddEditCloudStoragePlan = lazy(
  () => import('@/features/cloudStorage/AddEditCloudStoragePlan'),
);
const SubscriptionConfigurationPage = lazy(() =>
  import('@/features/subscriptionConfiguration').then((m) => ({
    default: m.SubscriptionConfigurationPage,
  })),
);
const AddEditSubscriptionConfigurationPage = lazy(() =>
  import('@/features/subscriptionConfiguration').then((m) => ({
    default: m.AddEditSubscriptionConfigurationPage,
  })),
);
const SubscriptionConfigurationHistoryPage = lazy(() =>
  import('@/features/subscriptionConfiguration').then((m) => ({
    default: m.SubscriptionConfigurationHistoryPage,
  })),
);
const ModulePricePage = lazy(() =>
  import('@/features/modulePrice').then((m) => ({ default: m.ModulePricePage })),
);
const AddEditModulePricePage = lazy(() =>
  import('@/features/modulePrice').then((m) => ({ default: m.AddEditModulePricePage })),
);
const CoinHistoryPage = lazy(() => import('@/features/coinHistory/CoinHistoryPage'));
const AddCoinPage = lazy(() => import('@/features/coinHistory/AddCoinPage'));
const StorageHistoryPage = lazy(() => import('@/features/coinHistory/StorageHistoryPage'));
const AddStoragePage = lazy(() => import('@/features/coinHistory/AddStoragePage'));
const ReportColumnsPage = lazy(() => import('@/features/reportColumns/ReportColumnsPage'));
const ReportColumnAddEditPage = lazy(
  () => import('@/features/reportColumns/ReportColumnAddEditPage'),
);
const SampleFilesPage = lazy(() =>
  import('@/features/sampleFiles').then((m) => ({ default: m.SampleFilesPage })),
);
const AddEditSampleFilePage = lazy(() =>
  import('@/features/sampleFiles').then((m) => ({ default: m.AddEditSampleFilePage })),
);
const PublicFilesPage = lazy(() =>
  import('@/features/publicFileManagement').then((m) => ({ default: m.PublicFilesPage })),
);
const SheetFieldPage = lazy(() => import('@/features/sheetField/SheetFieldPage'));
const AddEditSheetFieldPage = lazy(() => import('@/features/sheetField/AddEditSheetFieldPage'));
const MaintenancePage = lazy(() => import('@/features/maintenance/MaintenancePage'));
const AppMaintenancePage = lazy(() => import('@/features/maintenance/AppMaintenancePage'));
const MasterSettingPage = lazy(() => import('@/features/masterSetting/MasterSettingPage'));
const AddEditMasterSettingPage = lazy(
  () => import('@/features/masterSetting/AddEditMasterSettingPage'),
);

const AddEditMaintenancePage = lazy(() => import('@/features/maintenance/AddEditMaintenancePage'));
const AppAddEditMaintenancePage = lazy(
  () => import('@/features/maintenance/AppAddEditMaintenancePage'),
);
const EducationOfficerPage = lazy(() => import('@/features/educationOfficer/EducationOfficerPage'));
const AddEditEducationOfficerPage = lazy(
  () => import('@/features/educationOfficer/AddEditEducationOfficerPage'),
);
const DEODPEOPage = lazy(() => import('@/features/deoDpeo/DEODPEOPage'));
const AddEditDEODPEOPage = lazy(() => import('@/features/deoDpeo/AddEditDEODPEOPage'));
const TicketPage = lazy(() => import('@/features/tickets/TicketPage'));
const AddEditTicketPage = lazy(() => import('@/features/tickets/AddEditTicketPage'));
const TicketDetailsPage = lazy(() => import('@/features/tickets/TicketDetailsPage'));

// QA Config
const QuestionSourcePage = lazy(() => import('@/features/qaConfig/pages/QuestionSourcePage'));
const AnswerSourcePage = lazy(() => import('@/features/qaConfig/pages/AnswerSourcePage'));
const QuestionTypePage = lazy(() => import('@/features/qaConfig/pages/QuestionTypePage'));
const QuestionDifficultyLevelPage = lazy(
  () => import('@/features/qaConfig/pages/QuestionDifficultyLevelPage'),
);
const QuestionCognitiveLevelPage = lazy(
  () => import('@/features/qaConfig/pages/QuestionCognitiveLevelPage'),
);
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'));
const UserDetailPage = lazy(() => import('@/features/users/UserDetailPage'));
const WalletHistoryPage = lazy(() => import('@/features/institute/WalletHistoryPage'));
const DesignationPage = lazy(() =>
  import('@/features/designation').then((m) => ({ default: m.DesignationPage })),
);
const AddEditDesignationPage = lazy(() =>
  import('@/features/designation').then((m) => ({ default: m.AddEditDesignationPage })),
);
const NewDesignationPage = lazy(() =>
  import('@/features/newDesignation').then((m) => ({ default: m.NewDesignationPage })),
);
const AddEditNewDesignationPage = lazy(() =>
  import('@/features/newDesignation').then((m) => ({ default: m.AddEditNewDesignationPage })),
);
const DynamicVariablePage = lazy(() =>
  import('@/features/dynamicVariable').then((m) => ({ default: m.DynamicVariablePage })),
);
const AddEditDynamicVariablePage = lazy(() =>
  import('@/features/dynamicVariable').then((m) => ({ default: m.AddEditDynamicVariablePage })),
);
const DynamicVariableLabelPage = lazy(() =>
  import('@/features/dynamicVariable').then((m) => ({ default: m.DynamicVariableLabelPage })),
);
const AddEditDynamicVariableLabelPage = lazy(() =>
  import('@/features/dynamicVariable').then((m) => ({
    default: m.AddEditDynamicVariableLabelPage,
  })),
);
const CommunityGroupPage = lazy(() => import('@/features/communityGroup/CommunityGroupPage'));
const AddEditCommunityGroupPage = lazy(
  () => import('@/features/communityGroup/AddEditCommunityGroupPage'),
);
const ViewCommunityGroupPage = lazy(
  () => import('@/features/communityGroup/ViewCommunityGroupPage'),
);

const ApplicationManagementPage = lazy(() =>
  import('@/features/applicationManagement').then((m) => ({
    default: m.ApplicationManagementPage,
  })),
);
const UploadApkPage = lazy(() =>
  import('@/features/applicationManagement').then((m) => ({ default: m.UploadApkPage })),
);
const FrcDocsPage = lazy(() =>
  import('@/features/frcDocs').then((m) => ({ default: m.FrcDocsPage })),
);
const AddEditFrcDocPage = lazy(() =>
  import('@/features/frcDocs').then((m) => ({ default: m.AddEditFrcDocPage })),
);
const AddCategoryPage = lazy(() => import('@/features/kipiMarketplace/AddCategoryPage'));
const AttributePage = lazy(() => import('@/features/kipiMarketplace/AttributePage'));
const AddEditAttributePage = lazy(() => import('@/features/kipiMarketplace/AddEditAttributePage'));
const CompanyApprovalReqPage = lazy(
  () => import('@/features/kipiMarketplace/CompanyApprovalReqPage'),
);
const TepdsCategoryPage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.TepdsCategoryPage })),
);
const AddEditEvaluationCategoryPage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.AddEditEvaluationCategoryPage })),
);
const TepdsRatingScalesPage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.TepdsRatingScalesPage })),
);
const AddEditRatingScalePage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.AddEditRatingScalePage })),
);
const TepdsEvaluationTemplatesPage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.TepdsEvaluationTemplatesPage })),
);
const AddEditEvaluationTemplatePage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.AddEditEvaluationTemplatePage })),
);
const TepdsTemplateBuilderPage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.TepdsTemplateBuilderPage })),
);
const TepdsTemplateMapsPage = lazy(() =>
  import('@/features/tepds').then((m) => ({ default: m.TepdsTemplateMapsPage })),
);
const AddEditEvaluationTemplateDesignationMapPage = lazy(() =>
  import('@/features/tepds').then((m) => ({
    default: m.AddEditEvaluationTemplateDesignationMapPage,
  })),
);
const TepdsEvaluationWeightConfigurationsPage = lazy(() =>
  import('@/features/tepds').then((m) => ({
    default: m.TepdsEvaluationWeightConfigurationsPage,
  })),
);
const AddEditEvaluationWeightConfigurationPage = lazy(() =>
  import('@/features/tepds').then((m) => ({
    default: m.AddEditEvaluationWeightConfigurationPage,
  })),
);

// ──────────────────────────────────────────────
// Lazy imports — Accounting Configuration
// ──────────────────────────────────────────────
const FinancialYearsPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.FinancialYearsPage })),
);
const FinancialYearAddEditPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({
    default: m.FinancialYearAddEditPage,
  })),
);
const VoucherTypesPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.VoucherTypesPage })),
);
const VoucherTypeAddEditPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.VoucherTypeAddEditPage })),
);
const AccountGroupsPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.AccountGroupsPage })),
);
const AccountGroupAddEditPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({
    default: m.AccountGroupAddEditPage,
  })),
);
const AccountLedgersPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.AccountLedgersPage })),
);
const AccountLedgerAddEditPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({
    default: m.AccountLedgerAddEditPage,
  })),
);
const JournalMappingsPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.JournalMappingsPage })),
);
const JournalMappingAddEditPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({
    default: m.JournalMappingAddEditPage,
  })),
);
const CostCentersPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({ default: m.CostCentersPage })),
);
const CostCenterAddEditPage = lazy(() =>
  import('@/features/accountingConfiguration').then((m) => ({
    default: m.CostCenterAddEditPage,
  })),
);

const ThemePage = lazy(() => import('@/features/theme/ThemePage'));
const AddEditThemePage = lazy(() => import('@/features/theme/AddEditThemePage'));
const ThemeDetailPage = lazy(() => import('@/features/theme/ThemeDetailPage'));



// ──────────────────────────────────────────────
// Suspense wrapper
// ──────────────────────────────────────────────
const withSuspense = (Component: React.ComponentType) => (
  <Suspense fallback={<PageLoader />}>
    <ErrorBoundary>
      <Component />
    </ErrorBoundary>
  </Suspense>
);

export const router = createBrowserRouter([
  // ── Public routes ─────────────────────────────
  {
    element: <PublicRoute />,
    children: [
      { path: ROUTES.LOGIN, element: withSuspense(LoginPage) },
      { path: ROUTES.FORGOT_PASSWORD, element: withSuspense(ForgotPasswordPage) },
      { path: ROUTES.OTP, element: withSuspense(OtpPage) },
      { path: ROUTES.RESET_PASSWORD, element: withSuspense(ResetPasswordPage) },
    ],
  },
  // ── Protected routes ──────────────────────────
  {
    element: <PrivateRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Root redirect
          { path: ROUTES.MAIN, element: <Navigate to={ROUTES.DASHBOARD} replace /> },
          { path: ROUTES.DASHBOARD, element: withSuspense(DashboardPage) },
          { path: ROUTES.SUBSCRIPTION_DASHBOARD, element: withSuspense(SubscriptionDashboardPage) },
          { path: ROUTES.SUBSCRIPTION_ANALYSIS, element: withSuspense(SubscriptionAnalysisPage) },
          { path: ROUTES.ANALYTICS_DASHBOARD, element: withSuspense(DashboardAnalyticsPage) },
          { path: ROUTES.ANALYTICS_ENGAGEMENT, element: withSuspense(EngagementAnalyticsPage) },
          { path: ROUTES.ANALYTICS_VERSION, element: withSuspense(VersionAnalyticsPage) },
          { path: ROUTES.ANALYTICS_PATTERN, element: withSuspense(PatternAnalyticsPage) },
          { path: ROUTES.ANALYTICS_USER, element: withSuspense(UserAnalyticsPage) },
          { path: ROUTES.ANALYTICS_INSTITUTE, element: withSuspense(SchoolInstituteAnalyticsPage) },
          // Entity
          { path: ROUTES.ENTITY, element: withSuspense(EntityPage) },
          { path: ROUTES.ADD_ENTITY, element: withSuspense(AddEditEntityPage) },
          { path: ROUTES.EDIT_ENTITY, element: withSuspense(AddEditEntityPage) },
          { path: ROUTES.ENTITYTYPE, element: withSuspense(EntityTypePage) },
          { path: ROUTES.ADD_ENTITY_TYPE, element: withSuspense(AddEditEntityTypePage) },
          { path: ROUTES.EDIT_ENTITY_TYPE, element: withSuspense(AddEditEntityTypePage) },
          { path: ROUTES.ACADEMIC_CALENDAR, element: withSuspense(AcademicCalendarPage) },
          {
            path: ROUTES.ADD_ACADEMIC_CALENDAR,
            element: withSuspense(AddEditAcademicCalendarPage),
          },
          {
            path: ROUTES.EDIT_ACADEMIC_CALENDAR,
            element: withSuspense(AddEditAcademicCalendarPage),
          },
          { path: ROUTES.INDEX_MANAGEMENT, element: withSuspense(IndexManagementPage) },
          // Institute & Schools
          { path: ROUTES.INSTITUTE_LIST, element: withSuspense(InstituteListPage) },
          {
            path: ROUTES.DELETED_INSTITUTE_LIST,
            element: withSuspense(DeletedInstituteListPage),
          },
          { path: ROUTES.INSTITUTE_DETAILS, element: withSuspense(InstituteDetailPage) },
          { path: ROUTES.ASSIGN_PLAN, element: withSuspense(AssignPlanPage) },
          { path: ROUTES.WALLET_HISTORY, element: withSuspense(WalletHistoryPage) },
          { path: ROUTES.SCHOOL_LIST, element: withSuspense(SchoolListPage) },
          { path: ROUTES.DELETED_SCHOOL_LIST, element: withSuspense(DeletedSchoolListPage) },
          { path: ROUTES.SCHOOL_DETAILS, element: withSuspense(InstituteDetailPage) },
          { path: ROUTES.COLLEGE_LIST, element: withSuspense(CollegeListPage) },
          { path: ROUTES.DELETED_COLLEGE_LIST, element: withSuspense(DeletedCollegeListPage) },
          { path: ROUTES.COLLEGE_DETAILS, element: withSuspense(InstituteDetailPage) },
          { path: ROUTES.HOSPITAL_LIST, element: withSuspense(HospitalListPage) },
          { path: ROUTES.HOSPITAL_DETAILS, element: withSuspense(ApprovalDetailPage) },
          // QA Config
          { path: ROUTES.QA_QUESTION_SOURCE, element: withSuspense(QuestionSourcePage) },
          { path: ROUTES.QA_ANSWER_SOURCE, element: withSuspense(AnswerSourcePage) },
          { path: ROUTES.QA_QUESTION_TYPE, element: withSuspense(QuestionTypePage) },
          {
            path: ROUTES.QA_QUESTION_DIFFICULTY_LEVEL,
            element: withSuspense(QuestionDifficultyLevelPage),
          },
          {
            path: ROUTES.QA_QUESTION_COGNITIVE_LEVEL,
            element: withSuspense(QuestionCognitiveLevelPage),
          },
          { path: ROUTES.TEACHER_LIST, element: withSuspense(TeacherListPage) },
          { path: ROUTES.STUDENT_LIST, element: withSuspense(StudentListPage) },
          // Approvals
          { path: ROUTES.APPROVAL_SCHOOLS, element: withSuspense(SchoolApprovalsPage) },
          { path: ROUTES.APPROVAL_SCHOOL_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_INSTITUTES, element: withSuspense(InstituteApprovalsPage) },
          { path: ROUTES.APPROVAL_INSTITUTE_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_COLLEGES, element: withSuspense(CollegeApprovalsPage) },
          { path: ROUTES.APPROVAL_COLLEGE_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_TEACHERS, element: withSuspense(TeacherApprovalsPage) },
          { path: ROUTES.APPROVAL_TEACHER_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_STUDENTS, element: withSuspense(StudentApprovalsPage) },
          { path: ROUTES.APPROVAL_STUDENT_DETAILS, element: withSuspense(ApprovalDetailPage) },
          {
            path: ROUTES.APPROVAL_SPORTS_ACADEMIES,
            element: withSuspense(SportsAcademyApprovalsPage),
          },
          {
            path: ROUTES.APPROVAL_SPORTS_ACADEMY_DETAILS,
            element: withSuspense(ApprovalDetailPage),
          },
          { path: ROUTES.APPROVAL_TRAINERS, element: withSuspense(TrainerApprovalsPage) },
          { path: ROUTES.APPROVAL_TRAINER_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_ATHLETES, element: withSuspense(AthleteApprovalsPage) },
          { path: ROUTES.APPROVAL_ATHLETE_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_COMPANIES, element: withSuspense(CompanyApprovalReqPage) },
          { path: ROUTES.APPROVAL_COMPANY_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_AGENCIES, element: withSuspense(AgencyApprovalsPage) },
          { path: ROUTES.APPROVAL_AGENCY_DETAILS, element: withSuspense(ApprovalDetailPage) },
          { path: ROUTES.APPROVAL_HOSPITALS, element: withSuspense(HospitalApprovalsPage) },
          { path: ROUTES.APPROVAL_HOSPITAL_DETAILS, element: withSuspense(ApprovalDetailPage) },
          {
            path: ROUTES.APPROVAL_SPORTS_AMENITIES,
            element: withSuspense(SportsAmenitiesApprovalsPage),
          },
          {
            path: ROUTES.APPROVAL_SPORTS_AMENITY_DETAILS,
            element: withSuspense(ApprovalDetailPage),
          },
          // Expo
          { path: ROUTES.EXPO, element: withSuspense(ExpoListPage) },
          { path: ROUTES.ADD_EXPO, element: withSuspense(AddEditExpoPage) },
          { path: ROUTES.EDIT_EXPO, element: withSuspense(AddEditExpoPage) },
          { path: ROUTES.VIEW_EXPO, element: withSuspense(ExpoDetailPage) },
          { path: ROUTES.EXPO_STAFF, element: withSuspense(ExpoStaffPage) },
          { path: ROUTES.EXPO_GRID, element: withSuspense(ExpoGridPage) },
          { path: ROUTES.EXPO_STALL, element: withSuspense(ExpoStallPage) },
          { path: ROUTES.EXPO_STAGE_SLOT, element: withSuspense(ExpoStageSlotPage) },
          { path: ROUTES.ADD_EXPO_STAGE_SLOT, element: withSuspense(AddEditExpoStageSlotPage) },
          { path: ROUTES.EDIT_EXPO_STAGE_SLOT, element: withSuspense(AddEditExpoStageSlotPage) },
          // Users
          { path: ROUTES.SUB_ADMINS, element: withSuspense(SubAdminPage) },
          { path: ROUTES.ADD_SUB_ADMIN, element: withSuspense(SubAdminAddEditPage) },
          { path: ROUTES.EDIT_SUB_ADMIN, element: withSuspense(SubAdminAddEditPage) },
          { path: ROUTES.TEACHERS_LIST, element: withSuspense(TeachersPage) },
          { path: ROUTES.STUDENTS_LIST, element: withSuspense(StudentsPage) },
          { path: ROUTES.PARENTS_LIST, element: withSuspense(ParentsPage) },
          { path: ROUTES.INSTITUTE_ADMINS_LIST, element: withSuspense(InstituteAdminsPage) },
          {
            path: ROUTES.INSTITUTE_MASTER_ADMINS_LIST,
            element: withSuspense(InstituteMasterAdminsPage),
          },
          { path: ROUTES.SCHOOL_ADMINS_LIST, element: withSuspense(SchoolAdminsPage) },
          { path: ROUTES.ADMINS_LIST, element: withSuspense(AdminsPage) },
          { path: ROUTES.MASTER_ADMINS_LIST, element: withSuspense(MasterAdminsPage) },
          { path: ROUTES.DRIVERS_LIST, element: withSuspense(DriversPage) },
          { path: ROUTES.VENDORS_LIST, element: withSuspense(VendorsPage) },
          {
            path: ROUTES.SPORT_INSTITUTE_OWNERS_LIST,
            element: withSuspense(SportInstituteOwnersPage),
          },
          { path: ROUTES.SPORT_TRAINERS_LIST, element: withSuspense(SportTrainersPage) },
          { path: ROUTES.SPORT_ATHLETES_LIST, element: withSuspense(SportAthletesPage) },
          { path: ROUTES.DEO_DPEO_LIST, element: withSuspense(DeoDpeoPage) },
          { path: ROUTES.DEO_DPEO_STAFF_LIST, element: withSuspense(DeoDpeoStaffPage) },
          {
            path: ROUTES.AGENCY_MASTER_ADMINS_LIST,
            element: withSuspense(AgencyMasterAdminsPage),
          },
          { path: ROUTES.JOB_SEEKERS_LIST, element: withSuspense(JobSeekersPage) },
          // Roles
          { path: ROUTES.ROLE_PERMISSION, element: withSuspense(RolesAndPermissionPage) },
          {
            path: ROUTES.MAKE_ROLES_AND_PERMISSION,
            element: withSuspense(MakeRolesAndPermissionPage),
          },
          { path: ROUTES.PERMISSIONS_LIST, element: withSuspense(PermissionsPage) },
          { path: ROUTES.CREATE_PERMISSION_CODE, element: withSuspense(CreatePermissionPage) },
          { path: ROUTES.EDIT_PERMISSION_CODE, element: withSuspense(CreatePermissionPage) },
          {
            path: ROUTES.GLOBAL_PERMISSIONS_LIST,
            element: withSuspense(() => <PermissionsPage isGlobal />),
          },
          {
            path: ROUTES.CREATE_GLOBAL_PERMISSION_CODE,
            element: withSuspense(() => <CreatePermissionPage isGlobal />),
          },
          {
            path: ROUTES.EDIT_GLOBAL_PERMISSION_CODE,
            element: withSuspense(() => <CreatePermissionPage isGlobal />),
          },
          { path: ROUTES.ROLE_MANAGEMENT_LIST, element: withSuspense(RoleManagementPage) },
          { path: ROUTES.CREATE_ROLE_MANAGEMENT, element: withSuspense(CreateRoleManagementPage) },
          { path: ROUTES.EDIT_ROLE_MANAGEMENT, element: withSuspense(CreateRoleManagementPage) },
          {
            path: ROUTES.GLOBAL_ROLE_MANAGEMENT_LIST,
            element: withSuspense(() => <RoleManagementPage isGlobal />),
          },
          {
            path: ROUTES.CREATE_GLOBAL_ROLE_MANAGEMENT,
            element: withSuspense(() => <CreateRoleManagementPage isGlobal />),
          },
          {
            path: ROUTES.EDIT_GLOBAL_ROLE_MANAGEMENT,
            element: withSuspense(() => <CreateRoleManagementPage isGlobal />),
          },
          // App-specific Roles & Permissions
          { path: ROUTES.APP_PERMISSIONS_LIST, element: withSuspense(AppPermissionsPage) },
          { path: ROUTES.APP_CREATE_PERMISSION, element: withSuspense(AppCreatePermissionPage) },
          { path: ROUTES.APP_EDIT_PERMISSION, element: withSuspense(AppCreatePermissionPage) },
          { path: ROUTES.APP_ROLE_MANAGEMENT_LIST, element: withSuspense(AppRoleManagementPage) },
          { path: ROUTES.APP_CREATE_ROLE, element: withSuspense(AppCreateRoleManagementPage) },
          { path: ROUTES.APP_EDIT_ROLE, element: withSuspense(AppCreateRoleManagementPage) },
          // Content
          { path: ROUTES.ANNOUNCEMENT, element: withSuspense(AnnouncementPage) },
          { path: ROUTES.ADD_ANNOUNCEMENT, element: withSuspense(AddEditAnnouncementPage) },
          { path: ROUTES.EDIT_ANNOUNCEMENT, element: withSuspense(AddEditAnnouncementPage) },
          { path: ROUTES.VIEW_ANNOUNCEMENT, element: withSuspense(ViewAnnouncementPage) },
          { path: ROUTES.MAKE_AN_ANNOUNCEMENT, element: withSuspense(AddEditAnnouncementPage) },
          { path: ROUTES.BANNER, element: withSuspense(BannerPage) },
          { path: ROUTES.ADD_BANNER, element: withSuspense(AddEditBannerPage) },
          { path: ROUTES.EDIT_BANNER, element: withSuspense(AddEditBannerPage) },
          { path: ROUTES.ADVERTISEMENT, element: withSuspense(AdvertisementPage) },
          { path: ROUTES.ADD_ADVERTISEMENT, element: withSuspense(AddEditAdvertisementPage) },
          { path: ROUTES.EDIT_ADVERTISEMENT, element: withSuspense(AddEditAdvertisementPage) },
          { path: ROUTES.VIEW_ADVERTISEMENT, element: withSuspense(ViewAdvertisementPage) },

          { path: ROUTES.INFORMATION, element: withSuspense(InformationPage) },
          { path: ROUTES.ADD_INFORMATION, element: withSuspense(AddEditInformationPage) },
          { path: ROUTES.EDIT_INFORMATION, element: withSuspense(AddEditInformationPage) },
          { path: ROUTES.VIEW_INFORMATION, element: withSuspense(InformationDetailsPage) },
          { path: ROUTES.CREATE_INFORMATION, element: withSuspense(AddEditInformationPage) }, // Backward compat
          { path: ROUTES.INFORMATION_DETAILS, element: withSuspense(InformationDetailsPage) }, // Backward compat
          {
            path: ROUTES.TYPE_MANAGEMENT,
            element: withSuspense(TypeManagementPage),
            errorElement: <ErrorBoundary children={null} />,
          },
          {
            path: ROUTES.ADD_TYPE,
            element: withSuspense(AddEditTypePage),
            errorElement: <ErrorBoundary children={null} />,
          },
          {
            path: ROUTES.EDIT_TYPE,
            element: withSuspense(AddEditTypePage),
            errorElement: <ErrorBoundary children={null} />,
          },
          { path: ROUTES.DESIGNATION, element: withSuspense(DesignationPage) },
          { path: ROUTES.ADD_DESIGNATION, element: withSuspense(AddEditDesignationPage) },
          { path: ROUTES.EDIT_DESIGNATION, element: withSuspense(AddEditDesignationPage) },
          { path: ROUTES.NEW_DESIGNATION, element: withSuspense(NewDesignationPage) },
          { path: ROUTES.ADD_NEW_DESIGNATION, element: withSuspense(AddEditNewDesignationPage) },
          { path: ROUTES.EDIT_NEW_DESIGNATION, element: withSuspense(AddEditNewDesignationPage) },
          { path: ROUTES.DYNAMIC_VARIABLE, element: withSuspense(DynamicVariablePage) },
          { path: ROUTES.ADD_DYNAMIC_VARIABLE, element: withSuspense(AddEditDynamicVariablePage) },
          { path: ROUTES.EDIT_DYNAMIC_VARIABLE, element: withSuspense(AddEditDynamicVariablePage) },
          { path: ROUTES.DYNAMIC_VARIABLE_LABEL, element: withSuspense(DynamicVariableLabelPage) },
          {
            path: ROUTES.ADD_DYNAMIC_VARIABLE_LABEL,
            element: withSuspense(AddEditDynamicVariableLabelPage),
          },
          {
            path: ROUTES.EDIT_DYNAMIC_VARIABLE_LABEL,
            element: withSuspense(AddEditDynamicVariableLabelPage),
          },
          { path: ROUTES.COMMUNITY_GROUP, element: withSuspense(CommunityGroupPage) },
          { path: ROUTES.ADD_COMMUNITY_GROUP, element: withSuspense(AddEditCommunityGroupPage) },
          { path: ROUTES.EDIT_COMMUNITY_GROUP, element: withSuspense(AddEditCommunityGroupPage) },
          { path: ROUTES.VIEW_COMMUNITY_GROUP, element: withSuspense(ViewCommunityGroupPage) },

          { path: ROUTES.TERMS_AND_CONDITION, element: withSuspense(TermsAndConditionPage) },
          {
            path: ROUTES.ADD_TERMS_AND_CONDITION,
            element: withSuspense(AddEditTermsAndConditionPage),
          },
          {
            path: ROUTES.EDIT_TERMS_AND_CONDITION,
            element: withSuspense(AddEditTermsAndConditionPage),
          },
          {
            path: ROUTES.VIEW_TERMS_AND_CONDITION,
            element: withSuspense(ViewTermsAndConditionPage),
          },

          {
            path: ROUTES.CERTIFICATES_MANAGEMENT,
            element: withSuspense(CertificatesManagementPage),
          },
          { path: ROUTES.ADD_CERTIFICATE, element: withSuspense(AddEditCertificatePage) },
          { path: ROUTES.EDIT_CERTIFICATE, element: withSuspense(AddEditCertificatePage) },
          {
            path: ROUTES.CUSTOM_CERTIFICATES,
            element: withSuspense(CustomCertificatesPage),
          },
          {
            path: ROUTES.ADD_CUSTOM_CERTIFICATE,
            element: withSuspense(AddEditCustomCertificatePage),
          },
          {
            path: ROUTES.EDIT_CUSTOM_CERTIFICATE,
            element: withSuspense(AddEditCustomCertificatePage),
          },
          // E-Learning
          { path: ROUTES.E_LEARNING, element: withSuspense(ElearningPage) },
          { path: ROUTES.CLONE_COURSE, element: withSuspense(CloneCoursePage) },
          { path: ROUTES.COURSE_INDEX, element: withSuspense(CourseIndexPage) },
          { path: ROUTES.COURSE_PLAN, element: withSuspense(CourseSetPlanPage) },
          { path: ROUTES.COURSE_CREATE, element: withSuspense(CreateCoursePage) },
          { path: ROUTES.COURSE_UPLOAD_MATERIAL, element: withSuspense(CourseUploadMaterialPage) },
          { path: ROUTES.MERGE_COURSE_CREATE, element: withSuspense(MergeCourseCreatePage) },
          { path: ROUTES.MERGE_COURSE_INDEX, element: withSuspense(MergeCourseIndexPage) },
          { path: ROUTES.MERGE_COURSE_PLAN, element: withSuspense(MergeCoursePlanPage) },
          // Billing
          { path: ROUTES.SUBSCRIPTION_PLAN, element: withSuspense(AddEditSubscriptionPlanPage) },
          { path: ROUTES.VIEW_SUBSCRIPTION_PLAN, element: withSuspense(SubscriptionPlanPage) },
          { path: ROUTES.COIN_OFFER, element: withSuspense(CoinOfferPage) },
          { path: ROUTES.ADD_COIN_OFFER, element: withSuspense(AddEditCoinOfferPage) },
          { path: ROUTES.EDIT_COIN_OFFER, element: withSuspense(AddEditCoinOfferPage) },
          { path: ROUTES.COIN_HISTORY, element: withSuspense(CoinHistoryPage) },
          { path: ROUTES.ADD_COIN_HISTORY, element: withSuspense(AddCoinPage) },
          { path: ROUTES.STORAGE_HISTORY, element: withSuspense(StorageHistoryPage) },
          { path: ROUTES.ADD_STORAGE_HISTORY, element: withSuspense(AddStoragePage) },
          {
            path: ROUTES.SUBSCRIPTION_CONFIGURATION,
            element: withSuspense(SubscriptionConfigurationPage),
          },
          {
            path: ROUTES.ADD_SUBSCRIPTION_CONFIGURATION,
            element: withSuspense(AddEditSubscriptionConfigurationPage),
          },
          {
            path: ROUTES.EDIT_SUBSCRIPTION_CONFIGURATION,
            element: withSuspense(AddEditSubscriptionConfigurationPage),
          },
          {
            path: ROUTES.SUBSCRIPTION_CONFIGURATION_HISTORY,
            element: withSuspense(SubscriptionConfigurationHistoryPage),
          },
          { path: ROUTES.MODULE_PRICE, element: withSuspense(ModulePricePage) },
          { path: ROUTES.ADD_MODULE_PRICE, element: withSuspense(AddEditModulePricePage) },
          { path: ROUTES.EDIT_MODULE_PRICE, element: withSuspense(AddEditModulePricePage) },
          // System
          { path: ROUTES.CLOUD_STORAGE, element: withSuspense(CloudStoragePage) },
          { path: ROUTES.ADD_CLOUD_STORAGE, element: withSuspense(AddEditCloudStoragePlan) },
          { path: ROUTES.EDIT_CLOUD_STORAGE, element: withSuspense(AddEditCloudStoragePlan) },
          { path: ROUTES.REPORT_COLUMNS, element: withSuspense(ReportColumnsPage) },
          { path: ROUTES.CREATE_REPORT_COLUMNS, element: withSuspense(ReportColumnAddEditPage) },
          { path: ROUTES.SAMPLE_FILES, element: withSuspense(SampleFilesPage) },
          { path: ROUTES.EDIT_SAMPLE_FILE, element: withSuspense(AddEditSampleFilePage) },
          { path: ROUTES.PUBLIC_FILES, element: withSuspense(PublicFilesPage) },
          { path: ROUTES.SHEET_FIELD, element: withSuspense(SheetFieldPage) },
          { path: ROUTES.ADD_SHEET_FIELD, element: withSuspense(AddEditSheetFieldPage) },
          { path: ROUTES.EDIT_SHEET_FIELD, element: withSuspense(AddEditSheetFieldPage) },
          { path: ROUTES.MAINTENANCE, element: withSuspense(MaintenancePage) },
          { path: ROUTES.MASTER_SETTINGS, element: withSuspense(MasterSettingPage) },
          { path: ROUTES.ADD_MASTER_SETTING, element: withSuspense(AddEditMasterSettingPage) },
          { path: ROUTES.EDIT_MASTER_SETTING, element: withSuspense(AddEditMasterSettingPage) },

          { path: ROUTES.ADD_MAINTENANCE, element: withSuspense(AddEditMaintenancePage) },
          { path: ROUTES.EDIT_MAINTENANCE, element: withSuspense(AddEditMaintenancePage) },
          { path: ROUTES.APP_MAINTENANCE, element: withSuspense(AppMaintenancePage) },
          { path: ROUTES.ADD_APP_MAINTENANCE, element: withSuspense(AppAddEditMaintenancePage) },
          { path: ROUTES.EDIT_APP_MAINTENANCE, element: withSuspense(AppAddEditMaintenancePage) },
          { path: ROUTES.EDUCATION_OFFICER, element: withSuspense(EducationOfficerPage) },
          {
            path: ROUTES.ADD_EDUCATION_OFFICER,
            element: withSuspense(AddEditEducationOfficerPage),
          },
          {
            path: ROUTES.EDIT_EDUCATION_OFFICER,
            element: withSuspense(AddEditEducationOfficerPage),
          },
          { path: ROUTES.DEO_DPEO, element: withSuspense(DEODPEOPage) },
          {
            path: ROUTES.ADD_DEO_DPEO,
            element: withSuspense(AddEditDEODPEOPage),
          },
          {
            path: ROUTES.EDIT_DEO_DPEO,
            element: withSuspense(AddEditDEODPEOPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS,
            element: withSuspense(KipigovtAnnouncementsPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS_ADD,
            element: withSuspense(AddEditKipigovtAnnouncementPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS_EDIT,
            element: withSuspense(AddEditKipigovtAnnouncementPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS_VIEW,
            element: withSuspense(ViewKipigovtAnnouncementPage),
          },
          // Tickets
          { path: ROUTES.TICKET, element: withSuspense(TicketPage) },
          { path: ROUTES.ADD_TICKET, element: withSuspense(AddEditTicketPage) },
          { path: ROUTES.EDIT_TICKET, element: withSuspense(AddEditTicketPage) },
          { path: ROUTES.TICKET_DETAILS, element: withSuspense(TicketDetailsPage) },
          { path: ROUTES.USER_DETAILS, element: withSuspense(UserDetailPage) },
          // Profile
          { path: ROUTES.PROFILE, element: withSuspense(ProfilePage) },
          // Kipiverse Inquirys
          { path: ROUTES.KIPIVERSE_INQUIRYS, element: withSuspense(KipiverseInquirysPage) },
          { path: ROUTES.EDIT_KIPIVERSE_INQUIRY, element: withSuspense(EditKipiverseInquiryPage) },
          // Application Management
          { path: ROUTES.APPLICATION_MANAGEMENT, element: withSuspense(ApplicationManagementPage) },
          { path: ROUTES.UPLOAD_APK, element: withSuspense(UploadApkPage) },
          // Module Doc AI
          { path: ROUTES.MODULE_DOC_AI, element: withSuspense(ModuleDocAiPage) },
          { path: ROUTES.ADD_MODULE_DOC_AI, element: withSuspense(AddEditModuleDocAiPage) },
          { path: ROUTES.EDIT_MODULE_DOC_AI, element: withSuspense(AddEditModuleDocAiPage) },
          // FRC Docs
          { path: ROUTES.FRC_DOCS, element: withSuspense(FrcDocsPage) },
          { path: ROUTES.ADD_FRC_DOC, element: withSuspense(AddEditFrcDocPage) },
          { path: ROUTES.EDIT_FRC_DOC, element: withSuspense(AddEditFrcDocPage) },
          // Kipi Marketplace
          { path: ROUTES.MARKETPLACE_ADD_CATEGORY, element: withSuspense(AddCategoryPage) },
          { path: ROUTES.MARKETPLACE_ATTRIBUTE, element: withSuspense(AttributePage) },
          { path: ROUTES.MARKETPLACE_ATTRIBUTE_ADD, element: withSuspense(AddEditAttributePage) },
          { path: ROUTES.MARKETPLACE_ATTRIBUTE_EDIT, element: withSuspense(AddEditAttributePage) },
          {
            path: ROUTES.MARKETPLACE_COMPANY_APPROVAL,
            element: withSuspense(CompanyApprovalReqPage),
          },
          {
            path: ROUTES.MARKETPLACE_COMPANY_APPROVAL_DETAILS,
            element: withSuspense(ApprovalDetailPage),
          },
          // Kipi Arena
          { path: ROUTES.ARENA_SPORTS, element: withSuspense(SportsPage) },
          { path: ROUTES.ARENA_SPORTS_ADD, element: withSuspense(AddEditSportPage) },
          { path: ROUTES.ARENA_SPORTS_EDIT, element: withSuspense(AddEditSportPage) },

          { path: ROUTES.ARENA_SPORT_CATEGORIES, element: withSuspense(SportCategoriesPage) },
          {
            path: ROUTES.ARENA_SPORT_CATEGORIES_ADD,
            element: withSuspense(AddEditSportCategoryPage),
          },
          {
            path: ROUTES.ARENA_SPORT_CATEGORIES_EDIT,
            element: withSuspense(AddEditSportCategoryPage),
          },

          {
            path: ROUTES.ARENA_SPORT_SUB_CATEGORIES,
            element: withSuspense(SportSubCategoriesPage),
          },
          {
            path: ROUTES.ARENA_SPORT_SUB_CATEGORIES_ADD,
            element: withSuspense(AddEditSportSubCategoryPage),
          },
          {
            path: ROUTES.ARENA_SPORT_SUB_CATEGORIES_EDIT,
            element: withSuspense(AddEditSportSubCategoryPage),
          },

          { path: ROUTES.ARENA_SPORT_ROLES, element: withSuspense(SportRolesPage) },
          { path: ROUTES.ARENA_SPORT_ROLES_ADD, element: withSuspense(AddEditSportRolePage) },
          { path: ROUTES.ARENA_SPORT_ROLES_EDIT, element: withSuspense(AddEditSportRolePage) },

          { path: ROUTES.ARENA_EQUIPMENTS, element: withSuspense(EquipmentsPage) },
          { path: ROUTES.ARENA_EQUIPMENTS_ADD, element: withSuspense(AddEditEquipmentPage) },
          { path: ROUTES.ARENA_EQUIPMENTS_EDIT, element: withSuspense(AddEditEquipmentPage) },
          {
            path: ROUTES.ARENA_EQUIPMENTS_LINK_SPORTS,
            element: withSuspense(LinkEquipmentSportsPage),
          },

          { path: ROUTES.ARENA_FACILITY_MASTERS, element: withSuspense(FacilityMastersPage) },
          {
            path: ROUTES.ARENA_FACILITY_MASTERS_ADD,
            element: withSuspense(AddEditFacilityMasterPage),
          },
          {
            path: ROUTES.ARENA_FACILITY_MASTERS_EDIT,
            element: withSuspense(AddEditFacilityMasterPage),
          },
          {
            path: ROUTES.ARENA_FACILITY_MASTERS_LINK_SPORTS,
            element: withSuspense(LinkFacilitySportsPage),
          },

          { path: ROUTES.ARENA_FACILITY_AMENITIES, element: withSuspense(FacilityAmenitiesPage) },
          {
            path: ROUTES.ARENA_FACILITY_AMENITIES_ADD,
            element: withSuspense(AddEditFacilityAmenityPage),
          },
          {
            path: ROUTES.ARENA_FACILITY_AMENITIES_EDIT,
            element: withSuspense(AddEditFacilityAmenityPage),
          },
          {
            path: ROUTES.ARENA_FACILITY_AMENITIES_LINK_SPORTS,
            element: withSuspense(LinkAmenitySportsPage),
          },

          { path: ROUTES.ARENA_UNITS, element: withSuspense(UnitsPage) },
          { path: ROUTES.ARENA_UNITS_ADD, element: withSuspense(AddEditUnitPage) },
          { path: ROUTES.ARENA_UNITS_EDIT, element: withSuspense(AddEditUnitPage) },

          { path: ROUTES.ARENA_SKILLS, element: withSuspense(SkillsPage) },
          { path: ROUTES.ARENA_SKILLS_ADD, element: withSuspense(AddEditSkillPage) },
          { path: ROUTES.ARENA_SKILLS_EDIT, element: withSuspense(AddEditSkillPage) },

          { path: ROUTES.ARENA_AGE_GROUPS, element: withSuspense(AgeGroupsPage) },
          { path: ROUTES.ARENA_AGE_GROUPS_ADD, element: withSuspense(AddEditAgeGroupPage) },
          { path: ROUTES.ARENA_AGE_GROUPS_EDIT, element: withSuspense(AddEditAgeGroupPage) },

          { path: ROUTES.ARENA_SPORT_GROUP_DATA, element: withSuspense(SportGroupDataPage) },
          {
            path: ROUTES.ARENA_SPORT_GROUP_DATA_ADD,
            element: withSuspense(AddEditSportGroupDataPage),
          },
          {
            path: ROUTES.ARENA_SPORT_GROUP_DATA_EDIT,
            element: withSuspense(AddEditSportGroupDataPage),
          },

          { path: ROUTES.ARENA_ASSESSMENT_TESTS, element: withSuspense(AssessmentTestsPage) },
          {
            path: ROUTES.ARENA_ASSESSMENT_TESTS_ADD,
            element: withSuspense(AddEditAssessmentTestPage),
          },
          {
            path: ROUTES.ARENA_ASSESSMENT_TESTS_EDIT,
            element: withSuspense(AddEditAssessmentTestPage),
          },

          { path: ROUTES.ARENA_SPORT_INFOS, element: withSuspense(SportInfosPage) },
          { path: ROUTES.ARENA_SPORT_INFOS_ADD, element: withSuspense(AddEditSportInfoPage) },
          { path: ROUTES.ARENA_SPORT_INFOS_EDIT, element: withSuspense(AddEditSportInfoPage) },

          { path: ROUTES.ARENA_ANNOUNCEMENTS, element: withSuspense(ArenaAnnouncementsPage) },
          {
            path: ROUTES.ARENA_ANNOUNCEMENTS_ADD,
            element: withSuspense(AddEditArenaAnnouncementPage),
          },
          {
            path: ROUTES.ARENA_ANNOUNCEMENTS_EDIT,
            element: withSuspense(AddEditArenaAnnouncementPage),
          },
          {
            path: ROUTES.ARENA_ANNOUNCEMENTS_VIEW,
            element: withSuspense(ViewArenaAnnouncementPage),
          },

          // Kipigovt Announcements
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS,
            element: withSuspense(KipigovtAnnouncementsPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS_ADD,
            element: withSuspense(AddEditKipigovtAnnouncementPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS_EDIT,
            element: withSuspense(AddEditKipigovtAnnouncementPage),
          },
          {
            path: ROUTES.KIPIGOVT_ANNOUNCEMENTS_VIEW,
            element: withSuspense(ViewKipigovtAnnouncementPage),
          },

          // Vendor Announcements
          {
            path: ROUTES.VENDOR_ANNOUNCEMENTS,
            element: withSuspense(VendorAnnouncementsPage),
          },
          {
            path: ROUTES.VENDOR_ANNOUNCEMENTS_ADD,
            element: withSuspense(AddEditVendorAnnouncementPage),
          },
          {
            path: ROUTES.VENDOR_ANNOUNCEMENTS_EDIT,
            element: withSuspense(AddEditVendorAnnouncementPage),
          },
          {
            path: ROUTES.VENDOR_ANNOUNCEMENTS_VIEW,
            element: withSuspense(ViewVendorAnnouncementPage),
          },

          // TEPDS
          { path: ROUTES.TEPDS_CATEGORY, element: withSuspense(TepdsCategoryPage) },
          { path: ROUTES.TEPDS_CATEGORY_ADD, element: withSuspense(AddEditEvaluationCategoryPage) },
          {
            path: ROUTES.TEPDS_CATEGORY_EDIT,
            element: withSuspense(AddEditEvaluationCategoryPage),
          },

          { path: ROUTES.TEPDS_RATING_SCALES, element: withSuspense(TepdsRatingScalesPage) },
          { path: ROUTES.TEPDS_RATING_SCALES_ADD, element: withSuspense(AddEditRatingScalePage) },
          { path: ROUTES.TEPDS_RATING_SCALES_EDIT, element: withSuspense(AddEditRatingScalePage) },

          { path: ROUTES.TEPDS_TEMPLATES, element: withSuspense(TepdsEvaluationTemplatesPage) },
          {
            path: ROUTES.TEPDS_TEMPLATES_ADD,
            element: withSuspense(AddEditEvaluationTemplatePage),
          },
          {
            path: ROUTES.TEPDS_TEMPLATES_EDIT,
            element: withSuspense(AddEditEvaluationTemplatePage),
          },
          { path: ROUTES.TEPDS_TEMPLATE_BUILDER, element: withSuspense(TepdsTemplateBuilderPage) },
          { path: ROUTES.TEPDS_TEMPLATE_MAPS, element: withSuspense(TepdsTemplateMapsPage) },
          {
            path: ROUTES.TEPDS_TEMPLATE_MAPS_ADD,
            element: withSuspense(AddEditEvaluationTemplateDesignationMapPage),
          },
          {
            path: ROUTES.TEPDS_TEMPLATE_MAPS_EDIT,
            element: withSuspense(AddEditEvaluationTemplateDesignationMapPage),
          },
          {
            path: ROUTES.TEPDS_WEIGHT_CONFIGS,
            element: withSuspense(TepdsEvaluationWeightConfigurationsPage),
          },
          {
            path: ROUTES.TEPDS_WEIGHT_CONFIGS_ADD,
            element: withSuspense(AddEditEvaluationWeightConfigurationPage),
          },
          {
            path: ROUTES.TEPDS_WEIGHT_CONFIGS_EDIT,
            element: withSuspense(AddEditEvaluationWeightConfigurationPage),
          },

          // ── Accounting Configuration ─────────────────────────
          { path: ROUTES.ACCOUNTING_FINANCIAL_YEARS, element: withSuspense(FinancialYearsPage) },
          {
            path: ROUTES.ACCOUNTING_FINANCIAL_YEARS_ADD,
            element: withSuspense(FinancialYearAddEditPage),
          },
          {
            path: ROUTES.ACCOUNTING_FINANCIAL_YEARS_EDIT,
            element: withSuspense(FinancialYearAddEditPage),
          },
          { path: ROUTES.ACCOUNTING_VOUCHER_TYPES, element: withSuspense(VoucherTypesPage) },
          {
            path: ROUTES.ACCOUNTING_VOUCHER_TYPES_ADD,
            element: withSuspense(VoucherTypeAddEditPage),
          },
          {
            path: ROUTES.ACCOUNTING_VOUCHER_TYPES_EDIT,
            element: withSuspense(VoucherTypeAddEditPage),
          },
          { path: ROUTES.ACCOUNTING_ACCOUNT_GROUPS, element: withSuspense(AccountGroupsPage) },
          {
            path: ROUTES.ACCOUNTING_ACCOUNT_GROUPS_ADD,
            element: withSuspense(AccountGroupAddEditPage),
          },
          {
            path: ROUTES.ACCOUNTING_ACCOUNT_GROUPS_EDIT,
            element: withSuspense(AccountGroupAddEditPage),
          },
          { path: ROUTES.ACCOUNTING_ACCOUNT_LEDGERS, element: withSuspense(AccountLedgersPage) },
          {
            path: ROUTES.ACCOUNTING_ACCOUNT_LEDGERS_ADD,
            element: withSuspense(AccountLedgerAddEditPage),
          },
          {
            path: ROUTES.ACCOUNTING_ACCOUNT_LEDGERS_EDIT,
            element: withSuspense(AccountLedgerAddEditPage),
          },
          { path: ROUTES.ACCOUNTING_JOURNAL_MAPPINGS, element: withSuspense(JournalMappingsPage) },
          {
            path: ROUTES.ACCOUNTING_JOURNAL_MAPPINGS_ADD,
            element: withSuspense(JournalMappingAddEditPage),
          },
          {
            path: ROUTES.ACCOUNTING_JOURNAL_MAPPINGS_EDIT,
            element: withSuspense(JournalMappingAddEditPage),
          },
          { path: ROUTES.ACCOUNTING_COST_CENTERS, element: withSuspense(CostCentersPage) },
          {
            path: ROUTES.ACCOUNTING_COST_CENTERS_ADD,
            element: withSuspense(CostCenterAddEditPage),
          },
          {
            path: ROUTES.ACCOUNTING_COST_CENTERS_EDIT,
            element: withSuspense(CostCenterAddEditPage),
          },
          // ── Theme ─────────────────────────────────────────────
          { path: ROUTES.THEME, element: withSuspense(ThemePage) },
          { path: ROUTES.ADD_THEME, element: withSuspense(AddEditThemePage) },
          { path: ROUTES.EDIT_THEME, element: withSuspense(AddEditThemePage) },
          { path: ROUTES.VIEW_THEME, element: withSuspense(ThemeDetailPage) },
        ],
      },
    ],
  },
  // 404
  { path: '*', element: <Error404 /> },
]);
